import { useEffect, useState } from 'react';
import { apiClient } from '@/lib/apiClient';
import { useAuthStore } from '@/stores/authStore';

interface ThemeTokens {
  light?: Record<string, string>;
  dark?: Record<string, string>;
}

interface Branding {
  logoUrl: string | null;
  faviconUrl: string | null;
  primaryColor: string | null;
  accentColor: string | null;
  themeTokens?: ThemeTokens | null;
  fontFamily?: string | null;
}

/**
 * Style id for the dynamic per-tenant token sheet. Stories 19.2 + 47.3.
 *
 * We inject a single <style id="kairos-tenant-theme"> block that defines
 * the tenant's overrides under :root and html.dark, so toggling between
 * light and dark modes flips the right palette without re-fetching.
 */
const STYLE_ID = 'kairos-tenant-theme';

function applyThemeStyle(tokens: ThemeTokens | null | undefined) {
  let style = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
  if (!tokens) {
    style?.remove();
    return;
  }
  if (!style) {
    style = document.createElement('style');
    style.id = STYLE_ID;
    document.head.appendChild(style);
  }
  const blocks: string[] = [];
  if (tokens.light) {
    const decls = Object.entries(tokens.light)
      .map(([k, v]) => `${k}: ${v};`)
      .join(' ');
    blocks.push(`:root { ${decls} }`);
  }
  if (tokens.dark) {
    const decls = Object.entries(tokens.dark)
      .map(([k, v]) => `${k}: ${v};`)
      .join(' ');
    blocks.push(`html.dark { ${decls} }`);
  }
  style.textContent = blocks.join('\n');
}

function applyFontFamily(family: string | null | undefined) {
  const root = document.documentElement;
  if (family) {
    root.style.setProperty('--font-family-tenant', family);
    document.body.style.fontFamily = family;
  } else {
    root.style.removeProperty('--font-family-tenant');
    document.body.style.removeProperty('font-family');
  }
}

/**
 * Fetches per-tenant branding on login and applies:
 *   - logo + favicon
 *   - primary + accent colors (legacy quick-set)
 *   - full theme_tokens (light + dark CSS variable overrides)
 *   - optional font-family override
 *
 * Logout clears every tenant-specific override so a subsequent login
 * as a different tenant doesn't inherit the previous theme.
 */
export function useBranding() {
  const tokens = useAuthStore((s) => s.tokens);
  const [branding, setBranding] = useState<Branding | null>(null);

  useEffect(() => {
    if (!tokens) {
      setBranding(null);
      document.documentElement.style.removeProperty('--color-primary');
      document.documentElement.style.removeProperty('--color-accent');
      applyThemeStyle(null);
      applyFontFamily(null);
      return;
    }

    apiClient
      .get<Branding>('/branding')
      .then(({ data }) => {
        setBranding(data);
        if (data.primaryColor) {
          document.documentElement.style.setProperty(
            '--color-primary',
            data.primaryColor,
          );
        }
        if (data.accentColor) {
          document.documentElement.style.setProperty(
            '--color-accent',
            data.accentColor,
          );
        }
        applyThemeStyle(data.themeTokens);
        applyFontFamily(data.fontFamily);
        if (data.faviconUrl) {
          const link =
            document.querySelector<HTMLLinkElement>("link[rel='icon']") ||
            document.createElement('link');
          link.rel = 'icon';
          link.href = data.faviconUrl;
          document.head.appendChild(link);
        }
      })
      .catch(() => setBranding(null));
  }, [tokens]);

  return branding;
}
